import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import { Table } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Placeholder from '@tiptap/extension-placeholder'
import { Markdown } from 'tiptap-markdown'
import { common, createLowlight } from 'lowlight'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { Editor } from '@tiptap/react'
import tippy, { type Instance as TippyInstance } from 'tippy.js'
import EditorBubbleMenu from './EditorBubbleMenu'
import { ActiveBlockDecoration } from './extensions/ActiveBlockDecoration'
import { ActiveBlockFlattener } from './extensions/ActiveBlockFlattener'
import { WikiLinkSuggestion } from './extensions/WikiLinkSuggestion'
import { WikiLinkList, type WikiLinkItem, type WikiLinkListRef } from './WikiLinkList'
import { ImageUpload } from './extensions/ImageUpload'
import { TableAutoConvert } from './extensions/TableAutoConvert'
import { WikiLinkHighlight } from './extensions/WikiLinkHighlight'
import './editor-styles.css'

const lowlight = createLowlight(common)

interface MarkdownEditorProps {
  content: string
  onChange: (markdown: string) => void
  editable?: boolean
  placeholder?: string
  workspaceSlug?: string
  pageId?: number
  borderless?: boolean
}

export default function MarkdownEditor({
  content,
  onChange,
  editable = true,
  placeholder = 'Start writing...',
  workspaceSlug,
  pageId,
  borderless = false,
}: MarkdownEditorProps) {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const handleUpdate = useCallback(
    ({ editor }: { editor: Editor }) => {
      if (editor) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const storage = editor.storage as any
        onChangeRef.current(storage.markdown.getMarkdown())
      }
    },
    [],
  )

  const wikiLinkExtension = useMemo(() => {
    if (!workspaceSlug) return null

    return WikiLinkSuggestion.configure({
      suggestion: {
        items: async ({ query }: { query: string }) => {
          try {
            const res = await fetch(
              `/api/${workspaceSlug}/pages/search?q=${encodeURIComponent(query)}`
            )
            return (await res.json()) as WikiLinkItem[]
          } catch {
            return []
          }
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (): any => {
          let component: ReactRenderer<WikiLinkListRef> | null = null
          let popup: TippyInstance[] | null = null

          return {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onStart: (props: any) => {
              component = new ReactRenderer(WikiLinkList, {
                props,
                editor: props.editor as Editor,
              })

              if (!props.clientRect) return

              popup = tippy('body', {
                getReferenceClientRect: props.clientRect as () => DOMRect,
                appendTo: () => document.body,
                content: component.element,
                showOnCreate: true,
                interactive: true,
                trigger: 'manual',
                placement: 'bottom-start',
              })
            },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onUpdate: (props: any) => {
              component?.updateProps(props)
              if (popup?.[0] && props.clientRect) {
                popup[0].setProps({
                  getReferenceClientRect: props.clientRect as () => DOMRect,
                })
              }
            },
            onKeyDown: (props: { event: KeyboardEvent }) => {
              if (props.event.key === 'Escape') {
                popup?.[0]?.hide()
                return true
              }
              return component?.ref?.onKeyDown(props) ?? false
            },
            onExit: () => {
              popup?.[0]?.destroy()
              component?.destroy()
            },
          }
        },
      },
    })
  }, [workspaceSlug])

  const extensions = useMemo(() => {
    const exts = [
      StarterKit.configure({
        codeBlock: false,
      }),
      CodeBlockLowlight.configure({
        lowlight,
      }),
      Image,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        },
      }),
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
      Markdown.configure({
        html: true,
        breaks: true,
        tightLists: true,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      ActiveBlockDecoration,
      ActiveBlockFlattener,
      TableAutoConvert,
      WikiLinkHighlight.configure({
        workspaceSlug,
      }),
    ]

    if (wikiLinkExtension) {
      exts.push(wikiLinkExtension)
    }

    if (workspaceSlug && pageId) {
      exts.push(
        ImageUpload.configure({
          uploadUrl: `/api/${workspaceSlug}/uploads`,
          pageId,
        })
      )
    }

    return exts
  }, [placeholder, wikiLinkExtension, workspaceSlug, pageId])

  const editor = useEditor({
    extensions,
    content,
    editable,
    onUpdate: handleUpdate,
  })

  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable)
    }
  }, [editor, editable])

  return (
    <div className={borderless ? '' : 'overflow-hidden rounded-md border border-gray-300 bg-white'} data-testid="markdown-editor">
      {editor && editable && <EditorBubbleMenu editor={editor} />}
      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none px-4 py-3 focus-within:outline-none"
      />
    </div>
  )
}
